"use client";
import { useTranslations } from "@zapier/i18n";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Text,
} from "@zapier/design-system";
import { useMembers } from "@zapier/client";

export interface TransferOwnershipPromptProps {
  /** "move" names the destination folder; "duplicate" has no folder name to show, since the copy lands next to the source Zap. */
  action?: "move" | "duplicate";
  folderName?: string;
  folderOwnerId: number;
  assetCount: number;
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function TransferOwnershipPrompt({
  action = "move",
  folderName = "",
  folderOwnerId,
  assetCount,
  isPending = false,
  onCancel,
  onConfirm,
}: TransferOwnershipPromptProps) {
  const t = useTranslations("transfer-ownership-prompt");
  const { members } = useMembers({ ids: [folderOwnerId] });
  const ownerName =
    members[String(folderOwnerId)]?.fullName || t("fallbacks.owner-name");

  return (
    <ModalDialogRoot
      isOpen={true}
      isDismissable={!isPending}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isPending) onCancel();
      }}
    >
      <ModalDialogHeader textAlign="start">
        {t("header.transfer-to-owner", { ownerName })}
      </ModalDialogHeader>
      <ModalDialogBody textAlign="start">
        <Text tag="p">
          {t("messages.private-folder", { action, folderName, ownerName })}
        </Text>
        <Spacer height="1em" />
        <Text tag="p">
          {t("messages.what-happens", { action, count: assetCount, ownerName })}
        </Text>
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button variant="secondary" isDisabled={isPending} onPress={onCancel}>
          {t("buttons.cancel")}
        </Button>
        <Button isDisabled={isPending} onPress={onConfirm}>
          {t("buttons.confirm", { action })}
        </Button>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}
